import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema } from "@/lib/jsonld";

const AffiliationTerms = () => {
  const jsonLdSchemas = [
    generateWebPageSchema("Conditions du programme d'affiliation", seoConfig.affiliationTerms.description, "/conditions-affiliation"),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Conditions du programme d'affiliation", path: "/conditions-affiliation" }
    ])
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.affiliationTerms} jsonLd={jsonLdSchemas} />
      <Header />
      <main className="flex-1">
        {/* Hero Section - Titre principal */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] text-center">
              Termes et conditions du programme d'affiliation Unotif
            </h1>
          </div>
        </section>

        {/* Contenu des articles */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Dernière mise à jour : 18 février 2026
            </p>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le présent Accord constitue un contrat légal entre SEO FRENCH TOUCH LLC, société immatriculée aux États-Unis, dont le siège est situé au 539 W. Commerce St #6642, Dallas, TX 75208, États-Unis (ci-après « Unotif », « la Société », « nous »), et toute personne physique ou morale souhaitant participer au programme d'affiliation Unotif (ci-après l'« Affilié » ou « vous »).
            </p>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              En vous inscrivant au programme d'affiliation via notre plateforme de gestion (Tolt ou tout autre outil que nous désignons), vous reconnaissez avoir lu, compris et accepté sans réserve l'ensemble des présentes conditions.
            </p>

            {/* Article 1 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              1. Nature de la relation
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le présent Accord ne crée aucune relation de partenariat, d'agence, de franchise, de représentation commerciale ou de relation salariale entre Unotif et l'Affilié. Les parties agissent en qualité d'entrepreneurs indépendants. L'Affilié ne dispose d'aucune autorité pour engager la responsabilité juridique de Unotif, ni pour contracter en son nom.
            </p>

            {/* Article 2 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              2. Conditions d'admission
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Pour être admis au programme, vous devez être âgé d'au moins dix-huit (18) ans et disposer de la capacité juridique de conclure un contrat. Vous vous engagez à fournir des informations exactes, complètes et à jour lors de votre inscription. Unotif se réserve le droit d'accepter ou de refuser toute candidature, sans obligation de motivation, notamment si les supports de promotion utilisés sont jugés contraires à la loi, aux bonnes mœurs, à l'ordre public ou à l'image de la Société.
            </p>

            {/* Article 3 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              3. Fonctionnement du suivi et attribution
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Lorsqu'un utilisateur clique sur votre lien d'affiliation, un cookie de suivi est déposé pour une durée de trois cent soixante-cinq (365) jours. La commission est attribuée selon le principe du « dernier clic », ce qui signifie que si un prospect clique ultérieurement sur le lien d'un autre affilié avant son achat, la vente sera attribuée au dernier lien cliqué.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'attribution d'une commission suppose que le prospect accepte les cookies et que le système de suivi fonctionne normalement. Unotif ne saurait être tenu responsable en cas de blocage, suppression ou refus des cookies par l'utilisateur, ni en cas d'utilisation de dispositifs techniques empêchant le suivi.
            </p>

            {/* Article 4 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              4. Commissions
            </h2>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li><span className="font-semibold">Taux de commission :</span> 30 % de commission récurrente sur tous les paiements effectués par le client parrainé.</li>
              <li><span className="font-semibold">Base de calcul :</span> La commission est calculée sur le montant <span className="font-semibold">Hors Taxes (HT)</span> réellement encaissé par Unotif, après déduction des remises commerciales (ex: -20% sur les plans annuels) et des éventuels remboursements.</li>
              <li><span className="font-semibold">Nature Récurrente :</span> La commission est versée mensuellement ou annuellement tant que le client parrainé conserve un abonnement payant actif et ne résilie pas son service.</li>
            </ul>

            {/* Article 5 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              5. Validation et paiement
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les commissions sont soumises à une période de validation de trente (30) jours à compter du paiement effectué par le client, afin de tenir compte des délais de remboursement et de prévention de fraude. Le paiement est déclenché lorsque le solde cumulé atteint un minimum de cinquante euros (50 €).
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les paiements sont effectués via la plateforme d'affiliation désignée par Unotif. L'Affilié est seul responsable des frais bancaires, frais de réception ou charges fiscales applicables dans son pays de résidence. Unotif n'effectue aucune retenue à la source.
            </p>

            {/* Article 6 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              6. Pratiques interdites
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'auto-affiliation est strictement interdite. Il est formellement prohibé d'utiliser son propre lien d'affiliation pour souscrire à un abonnement Unotif. Toute tentative en ce sens entraînera l'annulation immédiate des commissions concernées et pourra conduire à la fermeture définitive du compte affilié.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Il est également interdit d'enchérir sur la marque « Unotif » ou toute variante orthographique dans le cadre de campagnes publicitaires payantes. Les pratiques de spam, de génération artificielle de clics, de manipulation de cookies, de redirection trompeuse ou toute autre méthode frauduleuse sont strictement prohibées et entraînent la résiliation immédiate du présent Accord.
            </p>

            {/* Article 7 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              7. Propriété intellectuelle
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif peut mettre à disposition de l'Affilié certains éléments de marque ou supports promotionnels. Ces éléments ne peuvent être utilisés que dans le cadre du programme d'affiliation. L'Affilié s'interdit toute utilisation susceptible de créer une confusion sur l'identité du site officiel de Unotif.
            </p>

            {/* Article 8 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              8. Limitation de responsabilité
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif ne garantit aucun niveau de revenu, ni aucune performance commerciale. Le succès de l'Affilié dépend exclusivement de ses propres actions de promotion. La Société ne saurait être tenue responsable d'interruptions temporaires du service, d'erreurs techniques indépendantes de sa volonté ou de cas de force majeure affectant le fonctionnement du programme.
            </p>

            {/* Article 9 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              9. Durée et résiliation
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le présent Accord entre en vigueur dès l'inscription de l'Affilié et demeure applicable jusqu'à sa résiliation. Chaque partie peut y mettre fin à tout moment. En cas de violation des présentes conditions, notamment en cas de fraude ou d'auto-affiliation, Unotif pourra résilier l'Accord sans préavis et annuler les commissions non encore versées.
            </p>

            {/* Article 10 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              10. Droit applicable et juridiction
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le présent Accord est régi par les lois de l'État du Texas, États-Unis. Tout litige relatif à son interprétation ou à son exécution relève de la compétence exclusive des tribunaux du comté de Dallas, Texas.
            </p>

            {/* Bloc adresse */}
            <div className="mt-12">
              <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-1">
                SEO FRENCH TOUCH LLC
              </p>
              <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-1">
                539 W. Commerce St #6642
              </p>
              <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-1">
                Dallas, TX 75208
              </p>
              <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                États-Unis
              </p>
            </div>

          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AffiliationTerms;
